import { useState, useEffect, useCallback } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc, where } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from './useAuth';

export function useTopics(sessionId, participants = null) {
  const { user } = useAuth();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // IMPORTANT: Only query if we have a valid sessionId and user
    if (!sessionId || typeof sessionId !== 'string' || !user?.uid) {
      setTopics([]);
      setLoading(false);
      return;
    }

    // Query topics where user is a participant (security rules require this)
    const q = query(
      collection(db, 'topics'),
      where('sessionId', '==', sessionId),
      where('participants', 'array-contains', user.uid),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const topicsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        setTopics(topicsData);
        setLoading(false);
      },
      (err) => {
        console.error('🔥 [useTopics] listener error:', err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [sessionId, user?.uid]);

  const addTopic = async (data) => {
    if (!user?.uid) {
      throw new Error('User must be logged in to add topics');
    }
    if (!sessionId || typeof sessionId !== 'string') {
      throw new Error('Cannot add topic without a valid session');
    }
    if (!participants || participants.length === 0) {
      throw new Error('Cannot add topic without participants');
    }

    await addDoc(collection(db, 'topics'), {
      ...data,
      sessionId,
      participants, // Multi-tenant: who can see this topic
      addedByUid: user.uid, // Multi-tenant: who added this topic
      discussed: false,
      createdAt: new Date()
    });
  };

  const updateTopic = async (topicId, data) => {
    await updateDoc(doc(db, 'topics', topicId), data);
  };

  const deleteTopic = async (topicId) => {
    await deleteDoc(doc(db, 'topics', topicId));
  };

  const toggleDiscussed = async (topicId, currentValue) => {
    await updateDoc(doc(db, 'topics', topicId), {
      discussed: !currentValue
    });
  };

  // Check if current user can edit/delete a topic
  const canEdit = useCallback((topic) => {
    if (!user?.uid) return false;
    // User can edit if they added this topic
    // For legacy data without addedByUid, use addedBy field
    if (topic.addedByUid) {
      return topic.addedByUid === user.uid;
    }
    // Fallback to legacy addedBy (name string)
    return false;
  }, [user?.uid]);

  return {
    topics,
    loading,
    addTopic,
    updateTopic,
    deleteTopic,
    toggleDiscussed,
    canEdit
  };
}
